// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Estoque/MovimentacaoDetalhe.tsx
//  Detalhe de uma movimentação de estoque: cabeçalho + grade de itens
// ════════════════════════════════════════════════════════════════════════════

import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useMovimentacaoDetalhe } from '@/features/estoque/hooks/useMovimentacao';

// ── helpers ──────────────────────────────────────────────────────────────────

const fmtQtd = (v: number) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(v);

const fmtMoeda = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const fmtDate = (s: string) =>
  new Date(s).toLocaleDateString('pt-BR');

function tipoVariant(id: number): 'default' | 'destructive' | 'secondary' | 'outline' {
  if (id === 1) return 'default';
  if (id === 2) return 'destructive';
  if (id === 3) return 'secondary';
  return 'outline';
}

// ── componente ───────────────────────────────────────────────────────────────

const MovimentacaoDetalhe = () => {
  const { id }    = useParams<{ id: string }>();
  const navigate  = useNavigate();
  const movId     = Number(id ?? 0);

  const { data: mov, isLoading, isError } = useMovimentacaoDetalhe(movId);

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !mov) {
    return (
      <div className="p-6 space-y-4">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Button>
        <p className="text-destructive">Movimentação não encontrada.</p>
      </div>
    );
  }

  const totalItens = mov.itens.reduce(
    (s, i) => s + Math.max(i.qtdEntrada, i.qtdSaida),
    0,
  );

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Movimentação {mov.numDocumento ? `— Doc. ${mov.numDocumento}` : `de ${fmtDate(mov.dtMovimento)}`}
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">Detalhe completo da movimentação</p>
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 space-y-1">
            <p className="text-xs text-muted-foreground">Tipo</p>
            <Badge variant={tipoVariant(mov.tipoMovimentoId)} className="text-sm">
              {mov.tipoMovimentoDesc}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 space-y-1">
            <p className="text-xs text-muted-foreground">Data do Movimento</p>
            <p className="font-semibold">{fmtDate(mov.dtMovimento)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 space-y-1">
            <p className="text-xs text-muted-foreground">Nº Documento</p>
            <p className="font-semibold">{mov.numDocumento ?? '—'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 space-y-1">
            <p className="text-xs text-muted-foreground">Total Movimentado</p>
            <p className="font-bold text-lg">{fmtMoeda(mov.totalMovimentado)}</p>
          </CardContent>
        </Card>
      </div>

      {mov.justificativa && (
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Justificativa</p>
            <p className="text-sm">{mov.justificativa}</p>
          </CardContent>
        </Card>
      )}

      {/* Itens */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Itens da Movimentação
            <span className="text-sm font-normal text-muted-foreground">
              ({mov.itens.length} produto{mov.itens.length !== 1 ? 's' : ''} — {fmtQtd(totalItens)} unidade{totalItens !== 1 ? 's' : ''})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead className="text-right">Saldo Ant. Entrada</TableHead>
                  <TableHead className="text-right">Qtd. Entrada</TableHead>
                  <TableHead className="text-right">Saldo Ant. Saída</TableHead>
                  <TableHead className="text-right">Qtd. Saída</TableHead>
                  <TableHead className="text-right">Valor Unit.</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mov.itens.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Nenhum item registrado
                    </TableCell>
                  </TableRow>
                ) : (
                  mov.itens.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.produtoDescricao}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">
                        {fmtQtd(item.saldoAtualEntrada)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-medium text-green-700">
                        {item.qtdEntrada > 0 ? `+${fmtQtd(item.qtdEntrada)}` : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">
                        {fmtQtd(item.saldoAtualSaida)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-medium text-red-700">
                        {item.qtdSaida > 0 ? `−${fmtQtd(item.qtdSaida)}` : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {fmtMoeda(item.valorUnitario)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {fmtMoeda(item.valorTotal)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Totais */}
          {mov.itens.length > 0 && (
            <div className="flex justify-end pt-4 border-t mt-4">
              <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
                <span className="text-muted-foreground text-right">Total de unidades movimentadas:</span>
                <span className="font-semibold text-right font-mono">{fmtQtd(totalItens)}</span>
                <span className="text-muted-foreground text-right">Valor total da movimentação:</span>
                <span className="font-bold text-right font-mono text-lg">{fmtMoeda(mov.totalMovimentado)}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MovimentacaoDetalhe;
